import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useBalanceContext } from "../../contexts/BalanceContext";
import apiClient from "../utils/apiClient";
import logger from "../utils/logger";

export const useUserVipStatus = () => {
  const { user, isLoggedIn } = useAuth();
  const { balances } = useBalanceContext();
  const [vipTiers, setVipTiers] = useState([]);
  const [userVipStatus, setUserVipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentFeathers = balances?.feathers || 0;

  // Fetch VIP tiers and user status
  useEffect(() => {
    if (!isLoggedIn || !user?.id) {
      setLoading(false);
      return;
    }

    const fetchVipData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use API client instead of direct Supabase calls
        try {
          // Fetch VIP tiers from API (if endpoint exists) or use fallback
          let tiersData = [];
          try {
            const tiersResponse = await apiClient.get("/api/casino/vip/tiers");
            tiersData = tiersResponse.data || tiersResponse || [];
          } catch (tiersError) {
            // Fallback: create basic tier structure with Egg as lowest tier
            tiersData = [
              {
                id: 1,
                name: "egg",
                display_name: "Egg",
                tier_order: 0,
                feathers_required: 0,
                daily_reward_gld: 50,
                daily_reward_cb: 5,
                feather_multiplier: 1,
                tier_color: "#F5F5DC",
                tier_icon: "🥚",
              },
            ];
          }

          // For user status, we'll calculate based on current feathers
          // since the user_vip_status table has RLS issues
          let userStatus = null;

          setVipTiers(tiersData || []);
          setUserVipStatus(userStatus);
        } catch (err) {
          logger.error("Error fetching VIP data via API:", err);
          setError(err.message);
          // Set fallback data - always include Egg tier as minimum
          setVipTiers([
            {
              id: 1,
              name: "egg",
              display_name: "Egg",
              tier_order: 0,
              feathers_required: 0,
              daily_reward_gld: 1000,
              daily_reward_cb: 0,
              daily_reward_feathers: 1,
              feather_multiplier: 1,
              tier_color: "#F5F5DC",
              tier_icon: "🥚",
            },
          ]);
          setUserVipStatus(null);
        }
      } catch (err) {
        logger.error("Error in fetchVipData:", err);
        setError(err.message);
        // Always provide Egg tier as fallback even on complete failure
        setVipTiers([
          {
            id: 1,
            name: "egg",
            display_name: "Egg",
            tier_order: 0,
            feathers_required: 0,
            daily_reward_gld: 1000,
            daily_reward_cb: 0,
            daily_reward_feathers: 1,
            feather_multiplier: 1,
            tier_color: "#F5F5DC",
            tier_icon: "🥚",
          },
        ]);
        setUserVipStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVipData();
  }, [isLoggedIn, user?.id]);

  // Calculate VIP progress and next tier
  const calculateVipProgress = () => {
    if (!vipTiers.length) {
      return {
        currentTier: null,
        nextTier: null,
        progressPercentage: 0,
        feathersToNext: 0,
        allTiers: vipTiers,
        totalFeathersEarned: 0,
        currentFeathersBalance: currentFeathers || 0,
        loginStreak: 0,
        longestStreak: 0,
        dailyRewardsClaimed: 0,
        tierAchievedAt: null,
      };
    }

    // Calculate current tier based on feathers (highest tier user qualifies for)
    // Ensure we always default to the lowest tier (Egg tier with tier_order 0)
    let currentTier =
      vipTiers.find((tier) => tier.tier_order === 0) || vipTiers[0]; // Default to Egg tier or first tier
    for (const tier of vipTiers) {
      if ((currentFeathers || 0) >= (tier.feathers_required || 0)) {
        currentTier = tier;
      } else {
        break;
      }
    }

    // If we have user VIP status, use that tier instead
    if (userVipStatus?.vip_tiers) {
      currentTier = userVipStatus.vip_tiers;
    }

    const currentTierIndex = vipTiers.findIndex(
      (tier) => tier.id === currentTier?.id,
    );
    const nextTier =
      currentTierIndex >= 0 && currentTierIndex < vipTiers.length - 1
        ? vipTiers[currentTierIndex + 1]
        : null;

    let progressPercentage = 0;
    let feathersToNext = 0;

    if (nextTier) {
      const currentTierFeathers = currentTier?.feathers_required || 0;
      const nextTierFeathers = nextTier.feathers_required || 0;
      const feathersInCurrentTier = nextTierFeathers - currentTierFeathers;
      const feathersProgress = Math.max(
        0,
        (currentFeathers || 0) - currentTierFeathers,
      );

      progressPercentage =
        feathersInCurrentTier > 0
          ? Math.min(100, (feathersProgress / feathersInCurrentTier) * 100)
          : 0;

      feathersToNext = Math.max(0, nextTierFeathers - (currentFeathers || 0));
    } else {
      // No next tier - check if this is Egg tier fallback or actual max tier
      if (currentTier?.name === "egg" && currentTier?.tier_order === 0) {
        // This is Egg tier fallback (VIP system issues), show minimal progress
        progressPercentage = Math.min(
          10,
          ((currentFeathers || 0) / 1000) * 100,
        );
        feathersToNext = Math.max(0, 1000 - (currentFeathers || 0));
      } else {
        // Actually at max tier
        progressPercentage = 100;
      }
    }

    return {
      currentTier,
      nextTier,
      progressPercentage,
      feathersToNext,
      allTiers: vipTiers,
      totalFeathersEarned:
        userVipStatus?.total_feathers_earned || currentFeathers || 0,
      currentFeathersBalance: currentFeathers || 0,
      loginStreak: userVipStatus?.current_login_streak || 0,
      longestStreak: userVipStatus?.longest_login_streak || 0,
      dailyRewardsClaimed: userVipStatus?.total_daily_rewards_claimed || 0,
      tierAchievedAt: userVipStatus?.tier_achieved_at,
    };
  };

  const vipProgress = calculateVipProgress();

  return {
    loading,
    error,
    ...vipProgress,
  };
};
